import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { StockCheckLocationOrderRequest, StockCheckLocationItemsOrderRequest } from '../../../app/services/api/generated';

// If you already have Section elsewhere, import it instead.
type Section = {
    id: string;                  // "loc-<id>" or "cat-<name>"
    type: 'location' | 'category';
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items?: any[];
    rowNumber?: number;
};

interface ReorderingContextType {
    isReorderingEnabled: boolean;
    setReorderingEnabled: (enabled: boolean) => void;
    isItemReorderingEnabled: boolean;
    setItemReorderingEnabled: (enabled: boolean) => void;
    hasOrderChanged: boolean;
    setOrderChanged: (changed: boolean) => void;
    hasItemOrderChanged: boolean;
    setItemOrderChanged: (changed: boolean) => void;
    isSavingOrder: boolean;
    setSavingOrder: (saving: boolean) => void;
    isSavingItemOrder: boolean;
    setSavingItemOrder: (saving: boolean) => void;
    handleSaveOrder: (
        stockCheckId: number,
        sectionsOrder: Section[],
        reorderCountZones: (stockCheckId: number, orderPayload: StockCheckLocationOrderRequest[]) => Promise<unknown>,
        onSuccess: () => void,
        onError: (error: unknown) => void
    ) => Promise<void>;
    handleSaveItemOrder: (
        stockCheckId: number,
        items: Array<{ locationId?: number; itemNumber?: string; order: number }>,
        reorderItemsInCountZones: (stockCheckId: number, orderPayload: StockCheckLocationItemsOrderRequest[]) => Promise<unknown>,
        onSuccess: () => void,
        onError: (error: unknown) => void
    ) => Promise<void>;
}

const ReorderingContext = createContext<ReorderingContextType | undefined>(undefined);

export const ReorderingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isReorderingEnabled, setReorderingEnabled] = useState<boolean>(false);
  const [isItemReorderingEnabled, setItemReorderingEnabled] = useState<boolean>(false);
  const [hasOrderChanged, setOrderChanged] = useState<boolean>(false);
  const [hasItemOrderChanged, setItemOrderChanged] = useState<boolean>(false);
  const [isSavingOrder, setSavingOrder] = useState<boolean>(false);
  const [isSavingItemOrder, setSavingItemOrder] = useState<boolean>(false);

  const handleSaveOrder = useCallback(async (
    stockCheckId: number,
    sectionsOrder: Section[],
    reorderCountZones: (stockCheckId: number, orderPayload: StockCheckLocationOrderRequest[]) => Promise<unknown>,
    onSuccess: () => void,
    onError: (error: unknown) => void,
  ): Promise<void> => {
    if (!stockCheckId) return;

    setSavingOrder(true);

    const orderPayload: StockCheckLocationOrderRequest[] = sectionsOrder?.map((section, index) => {
      if (section.type === 'location') {
        const locationId = parseInt(section.id.replace('loc-', ''), 10);
        return {
          locationId,
          order: index,
        };
      } else {
        return {
          categoryName: section.name,
          order: index,
        };
      }
    });

    try {
      await reorderCountZones(stockCheckId, orderPayload);
      setReorderingEnabled(false);
      setOrderChanged(false);
      onSuccess();
    } catch (error) {
      onError(error);
    } finally {
      setSavingOrder(false);
    }
  }, []);

  const handleSaveItemOrder = useCallback(async (
    stockCheckId: number,
    items: Array<{ locationId?: number; itemNumber?: string; order: number }>,
    reorderItemsInCountZones: (stockCheckId: number, orderPayload: StockCheckLocationItemsOrderRequest[]) => Promise<unknown>,
    onSuccess: () => void,
    onError: (error: unknown) => void,
  ): Promise<void> => {
    if (!stockCheckId || items.length === 0) return;

    setSavingItemOrder(true);

    try {
      const orderPayload: StockCheckLocationItemsOrderRequest[] = items
        .filter(item => item.itemNumber)
        .map(item => ({
          locationId: item.locationId,
          itemNumber: item.itemNumber,
          order: item.order,
        }));

      await reorderItemsInCountZones(stockCheckId, orderPayload);

      setItemReorderingEnabled(false);
      setItemOrderChanged(false);
      onSuccess();
    } catch (error) {
      console.error('Error saving item order:', error);
      onError(error);
    } finally {
      setSavingItemOrder(false);
    }
  }, []);

  const value: ReorderingContextType = {
    isReorderingEnabled,
    setReorderingEnabled,
    isItemReorderingEnabled,
    setItemReorderingEnabled,
    hasOrderChanged,
    setOrderChanged,
    hasItemOrderChanged,
    setItemOrderChanged,
    isSavingOrder,
    setSavingOrder,
    isSavingItemOrder,
    setSavingItemOrder,
    handleSaveOrder,
    handleSaveItemOrder,
  };

  return <ReorderingContext.Provider value={value}>{children}</ReorderingContext.Provider>;
};

export const useReordering = (): ReorderingContextType => {
  const context = useContext(ReorderingContext);
  if (context === undefined) {
    throw new Error('useReordering must be used within a ReorderingProvider');
  }
  return context;
};